import Workflow from 'models/Workflow';
import { ActionCompletionInput, WorkflowValuesInput } from 'services/api/workflows';

import {
  CollectedFacility,
  PortalSectionField,
  portalSectionFields,
  REFERRAL_FIELD_SECTIONS,
  ReferralFieldSection,
  VALUE_NAMES,
} from '../constants';

import { FieldState, isPresent, outstandingFacilityMapping, outstandingRetrieval } from './fieldResolver';

/**
 * The slideout's one gesture, as a request body (OLIO-3022).
 *
 * The user fills a name and resolves a facility; underneath that is a retrieval completion and a
 * facility-mapping completion, and they must not be able to tell. So the payload is built here,
 * from the field states and the form, rather than assembled by whichever component happens to own
 * the control — a section that posted its own PATCH would put the two halves of one gesture in
 * two requests, either of which can fail on its own.
 */

// What the person has in front of them: the current contents of every editable row, and the
// facility answer in whichever shape that section is in. Not a diff — the value shown IS the value
// submitted, which is why an unedited `Abigail,Smith` goes back exactly as drawn.
export interface TaskSlideoutForm {
  // Row value name -> what the field holds now.
  values: Record<string, string>;
  // The mapping action's answer: CollectedValue entries, `{externalValue, mappedValue}` for a
  // portal string being resolved and `{mappedValue}` for an outright pick.
  facilityResolutions?: unknown[];
  // The facilities picked when RETRIEVAL owns the facility section (nothing has been collected
  // yet, so there is nothing to map).
  facilities?: CollectedFacility[];
  // facilityPicksComplete's verdict, carried on the form because this module never sees the
  // picks themselves. `facilityResolutions` alone can't answer it: it is the FILTERED pick
  // list, so with two unmapped portal strings one pick makes it non-empty — submitting then
  // would complete the mapping and drop the second string for good.
  facilityComplete?: boolean;
}

export const EMPTY_TASK_FORM: TaskSlideoutForm = { values: {} };

// What a row holds after the person's edits: the form when the row is editable, the resolved
// value otherwise. Presence marks and section chips both read through this, so a chip flips to
// `Complete` as the blank it named gets filled rather than only after a round-trip.
export function pendingValue(field: FieldState | undefined, form: TaskSlideoutForm): unknown {
  if (!field) return undefined;
  if (field.name === VALUE_NAMES.FACILITIES) {
    if (form.facilityResolutions) return form.facilityResolutions;
    if (form.facilities) return form.facilities;
    return field.value;
  }
  return field.editable ? (form.values[field.name] ?? field.value) : field.value;
}

export function isFieldSatisfied(field: FieldState | undefined, form: TaskSlideoutForm): boolean {
  if (!field) return false;
  if (field.name === VALUE_NAMES.FACILITIES) {
    // The mapping step's own completeness check, not mere presence of picks (see
    // TaskSlideoutForm.facilityComplete).
    if (form.facilityResolutions) return form.facilityComplete ?? isPresent(form.facilityResolutions);
    // The pre-mapping review's check: a staged string with no pick makes `facilities`
    // non-empty but the answer incomplete — mere presence would enable a submit that
    // immediately stalls on the mapping step.
    if (form.facilities && form.facilityComplete !== undefined) return form.facilityComplete;
    // A facility row nobody can act on must not gate the footer: with neither action
    // outstanding there is no control that could ever satisfy it — the run's own pipeline
    // owns the answer now, and holding the submit hostage to it dead-ends the whole task.
    if (!field.editable) return true;
  }
  // A flagged action naming this value keeps it unsatisfied until something is written for it —
  // but once the person types one, the mark follows what they typed.
  if (!field.editable) return field.present;
  return isPresent(pendingValue(field, form));
}

export function sectionFields(section: ReferralFieldSection): PortalSectionField[] {
  return portalSectionFields(section);
}

export function isSectionComplete(
  section: ReferralFieldSection,
  fields: Map<string, FieldState>,
  form: TaskSlideoutForm
): boolean {
  return sectionFields(section).every((field) => isFieldSatisfied(fields.get(field.name), form));
}

export function everySectionComplete(fields: Map<string, FieldState>, form: TaskSlideoutForm): boolean {
  return REFERRAL_FIELD_SECTIONS.every((section) => isSectionComplete(section, fields, form));
}

// The demographics a retrieval completion carries. Name parts collapse back into the composite
// they came from: the api resolves it (Workflow::NormalizeNameOutput), so no lexical guessing
// happens in the browser and the single-name portal case keeps its home.
function typedOutputs(fields: Map<string, FieldState>, form: TaskSlideoutForm, actionId: string): WorkflowValuesInput {
  const outputs: WorkflowValuesInput = {};
  const name = { first: '', last: '' };
  let named = false;

  sectionFields('demographics').forEach((field) => {
    const state = fields.get(field.name);
    if (!state?.editable || state.target?.kind !== 'actionOutputs' || state.target.actionId !== actionId) return;

    const value = String(pendingValue(state, form) ?? '').trim();
    if (!field.composite) {
      if (value) outputs[field.name] = value;
      return;
    }
    name[field.composite.part] = value;
    named ||= !!value;
  });

  if (named) outputs[VALUE_NAMES.PATIENT_NAME] = name;
  return outputs;
}

/**
 * The actions this one gesture completes, in the order the api applies them.
 *
 * Retrieval's staged outputs are spread back verbatim before the typed ones: `complete!` REPLACES
 * the whole staged set with what is posted, so a submit carrying only the edited fields would
 * silently drop every capture the person didn't touch. `facilities: []` leads for the same reason
 * retrieval's own default does — the mapping step gates on the value being present, and sending
 * nothing would leave it waiting forever.
 */
export function buildActionCompletions(
  workflow: Workflow,
  fields: Map<string, FieldState>,
  form: TaskSlideoutForm
): ActionCompletionInput[] {
  const completions: ActionCompletionInput[] = [];

  const retrieval = outstandingRetrieval(workflow);
  if (retrieval) {
    completions.push({
      id: retrieval.id,
      outputs: {
        [VALUE_NAMES.FACILITIES]: form.facilities ?? [],
        ...retrieval.stagedOutputs(),
        ...typedOutputs(fields, form, retrieval.id),
        // After the staged spread: a facility the person picked here is the answer, and the
        // staged (empty) collection is not.
        ...(form.facilities ? { [VALUE_NAMES.FACILITIES]: form.facilities } : {}),
      },
    });
  }

  // Only a COMPLETE answer: `complete!` replaces the mapping's whole answer with what is
  // posted, so a partial pick list would resolve one portal string and lose the rest for good.
  const mapping = outstandingFacilityMapping(workflow);
  if (mapping && form.facilityResolutions && form.facilityComplete !== false) {
    completions.push({ id: mapping.id, outputs: { facilityResolutions: form.facilityResolutions } });
  }

  return completions;
}

/**
 * Corrections to values nothing is asking for, posted as plain workflow values alongside any
 * completions. The api applies `values` BEFORE completions in the same transaction, which is the
 * order this surface needs: a fix to a flagged action's value is committed by the time that
 * action's completion is applied.
 *
 * Only what the person actually typed. Sending every resolved row back would re-stamp machine
 * captures as manual-rank entries the person never made — and manual outranks everything, so that
 * relabelling would stick and quietly outrank later integration writes.
 */
export function buildValues(fields: Map<string, FieldState>, form: TaskSlideoutForm): WorkflowValuesInput {
  const values: WorkflowValuesInput = {};

  fields.forEach((state) => {
    if (state.target?.kind !== 'workflowValues') return;
    const typed = form.values[state.name];
    if (typed === undefined) return;

    values[state.target.valueName] = typed.trim();
  });

  return values;
}
